// Listes paginées par curseur des routes métier (L0-C) et dépôt de fichiers.
import { ETATS_DOSSIER_FINAUX } from '@cc/contrats';
import { LIBELLES_ETATS_DOSSIER, type DocumentResume, type ListePaginee } from '@cc/contrats/ui-metier';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { requete } from '../../api/client';

/** Pile de curseurs : page suivante par le curseur renvoyé, retour par la pile. */
export function useListePaginee<T>(cle: string, chemin: string, parametres: Record<string, string>, actif = true) {
  const [curseurs, setCurseurs] = useState<string[]>([]);
  const curseur = curseurs.at(-1);
  const signature = JSON.stringify(parametres);
  const [signatureCourante, setSignatureCourante] = useState(signature);
  if (signature !== signatureCourante) {
    // Un changement de filtre ramène à la première page.
    setSignatureCourante(signature);
    setCurseurs([]);
  }
  const q = useQuery({
    queryKey: [cle, 'liste', parametres, curseur ?? null],
    queryFn: ({ signal }) => requete<ListePaginee<T>>(chemin, { signal, parametres: { ...parametres, apres: curseur } }),
    enabled: actif,
    retry: false,
    placeholderData: keepPreviousData,
  });
  return {
    requete: q,
    elements: q.data?.elements ?? [],
    pagination: {
      aPrecedent: curseurs.length > 0,
      aSuivant: Boolean(q.data?.suivant),
      enCours: q.isFetching,
      onPrecedent: () => setCurseurs((c) => c.slice(0, -1)),
      onSuivant: () => q.data?.suivant && setCurseurs((c) => [...c, q.data?.suivant as string]),
      resume: q.data ? `Page ${curseurs.length + 1}${q.data.total !== undefined ? `, ${q.data.total.toLocaleString('fr-FR')} au total` : ''}` : undefined,
    },
  };
}

/** Dépôt d'un fichier (POST /documents, multipart) rattaché à un objet. */
export async function deposerDocument(fichier: File, objetType: string, objetId: string): Promise<DocumentResume> {
  const f = new FormData();
  f.set('fichier', fichier);
  f.set('titre', fichier.name);
  f.set('objet_type', objetType);
  f.set('objet_id', objetId);
  return requete<DocumentResume>('/documents', { methode: 'POST', corps: f });
}

export const TYPES_ACCEPTES = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx,.odt';
export const TAILLE_MAX_OCTETS = 10 * 1024 * 1024;

/** URL d'accès au contenu d'un document : la route renvoie une URL signée (10 minutes) ou le flux lui-même. */
export async function urlContenu(id: string): Promise<string> {
  const r = await requete<Response>(`/documents/${id}/contenu`, { brut: true });
  if ((r.headers.get('content-type') ?? '').includes('json')) {
    const corps = (await r.json()) as { url?: string };
    if (corps.url) return corps.url;
  }
  return URL.createObjectURL(await r.blob());
}

/**
 * Étapes vers lesquelles un retour motivé est possible (POST /dossiers/:id/retour, champ « vers ») :
 * états déjà franchis, du plus récent au plus ancien, sans l'état courant, les états terminaux ni les
 * états d'avant l'enregistrement (le serveur refuse tout état non parcouru).
 */
export function etapesDeRetour(evenements: { etat_apres: string }[], etatCourant: string): string[] {
  const exclus = new Set<string>([etatCourant, 'brouillon', 'depose', ...ETATS_DOSSIER_FINAUX]);
  const etapes: string[] = [];
  for (const e of [...evenements].reverse()) {
    if (!exclus.has(e.etat_apres) && !etapes.includes(e.etat_apres)) etapes.push(e.etat_apres);
  }
  return etapes;
}

/** Libellé d'un état de dossier, y compris les états générés par l'éditeur de démarches (en_instruction_<étape>, en_validation_<étape>, en_attente_decision_<étape>). */
export function libelleEtatDossier(etat: string): string {
  const connu = LIBELLES_ETATS_DOSSIER[etat];
  if (connu) return connu;
  if (etat.startsWith('en_instruction_')) return 'En instruction';
  if (etat.startsWith('en_validation_')) return 'En validation';
  if (etat.startsWith('en_attente_decision_')) return 'En attente de décision';
  return etat;
}
